import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface LightboxPhoto {
  id: number;
  url: string;
  category?: string;
  label?: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  initialIndex: number;
  onClose: () => void;
}

export function PhotoLightbox({ photos, initialIndex, onClose }: PhotoLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const safeIndex = Math.max(0, Math.min(currentIndex, photos.length - 1));

  const goNext = useCallback(() => {
    setCurrentIndex(i => (i + 1) % photos.length);
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex(i => (i - 1 + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, goNext, goPrev]);

  if (!photos.length) return null;
  const photo = photos[safeIndex];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="photo-lightbox"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="lightbox-backdrop"
      />

      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[101] text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 z-[101] text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 z-[101] text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <div className="relative z-[101] max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img
          src={photo.url}
          alt={photo.label || "Cleaning photo"}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
        <div className="flex items-center gap-3 text-white/90 text-sm">
          {photo.label && <span>{photo.label}</span>}
          <span className="text-white/50">{safeIndex + 1} / {photos.length}</span>
        </div>
      </div>
    </div>
  );
}

export function useLightbox() {
  const [lightboxState, setLightboxState] = useState<{ photos: LightboxPhoto[]; index: number } | null>(null);

  const openLightbox = useCallback((photos: LightboxPhoto[], index: number) => {
    setLightboxState({ photos, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxState(null);
  }, []);

  return { lightboxState, openLightbox, closeLightbox };
}
